import { useCallback, useEffect, useState } from 'react';
import type { BillingStatusDTO } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { useAsyncData } from '../directory/useAsyncData';
import { t } from '../../lib/i18n';

/**
 * iyzico abonelik durumu ve işlemleri.
 *
 * Ödeme tarayıcıda tamamlandığı için uygulamaya dönüldüğünde (pencere odağı)
 * durum kendiliğinden tazeleniyor.
 */
export function useBilling(onNotify: (kind: ToastKind, text: string) => void) {
  const [revision, setRevision] = useState(0);
  const status = useAsyncData<BillingStatusDTO>(() => window.zabit.getBillingStatus(), revision);
  const [isBusy, setIsBusy] = useState(false);

  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload]);

  const cancel = useCallback(async () => {
    const confirmed = window.confirm(
      t(
        'Aboneliğin iptal edilsin mi? Mevcut dönem sonuna kadar kullanmaya devam edebilirsin.',
        'Cancel your subscription? You can keep using it until the end of the current period.'
      )
    );
    if (!confirmed) return;
    setIsBusy(true);
    try {
      await window.zabit.cancelBilling();
      onNotify('success', t('Abonelik iptal edildi.', 'Subscription canceled.'));
    } catch (err) {
      onNotify('error', (err as Error).message || t('Abonelik iptal edilemedi.', 'Could not cancel the subscription.'));
    } finally {
      setIsBusy(false);
      reload();
    }
  }, [onNotify, reload]);

  const refresh = useCallback(async () => {
    setIsBusy(true);
    try {
      await window.zabit.refreshBilling();
    } catch (err) {
      onNotify('error', (err as Error).message);
    } finally {
      setIsBusy(false);
      reload();
    }
  }, [onNotify, reload]);

  const subscription = status.data?.subscription;

  return {
    status,
    subscription,
    isSubscribed: subscription?.status === 'ACTIVE',
    isBusy,
    cancel,
    refresh,
    reload,
  };
}
